import getHelper from '../../helpers/services/getHelper';
import Helper from '../../helpers/models/Helper';
import * as initialHelpers from '../../helpers';

import type HttpMethod from '../types/HttpMethod';
import type Interceptor from '../types/Interceptor';
import type InterceptorRequestData from '../types/InterceptorRequestData';
import type RequestHeaders from '../types/RequestHeaders';
import type Response from '../types/Response';
import type ResponseBody from '../types/ResponseBody';
import type RequestInteface from '../types/RequestInterface';
import type ParentEntity from '../types/ParentEntity';

import Request from './Request';

import type { ClientApiInterface, ClientApiOptions, ClientLanguage, ApiVersion } from '../../..';

export const ROOT_URL = 'https://api.openclassrooms.com';
export const DEFAULT_VERSION = '0.1';

type InitialHelpers = typeof initialHelpers;
type RootHelperNames = keyof InitialHelpers;

type ClientHelpers = {
  [rootHelperName in RootHelperNames]: {
    [helperName in keyof InitialHelpers[rootHelperName]]: InstanceType<
      InitialHelpers[rootHelperName][helperName]
    >;
  };
};

type KeysOfUnion<T> = T extends T ? keyof T : never;

class ClientApi implements ClientApiInterface<ClientHelpers> {
  helpers: ClientHelpers;

  language: ClientLanguage;

  version: ApiVersion;

  rootUrl: string;

  requestInterceptors: Interceptor<InterceptorRequestData>[];

  responseInterceptors: Interceptor<Response>[];

  accessToken: string | null;

  constructor({
    language = 'en',
    rootUrl = ROOT_URL,
    version = DEFAULT_VERSION,
  }: ClientApiOptions = {}) {
    this.language = language;
    this.version = version;
    this.rootUrl = rootUrl;
    this.requestInterceptors = [];
    this.responseInterceptors = [];
    this.accessToken = null;

    const generatedHelpers: Partial<ClientHelpers> = {};

    (Object.keys(initialHelpers) as RootHelperNames[]).forEach(
      (rootHelperName: RootHelperNames) => {
        const scopeHelpers = initialHelpers[rootHelperName];
        type HelperName = KeysOfUnion<InitialHelpers[RootHelperNames]>;

        const currentlyGeneratedHelper: Partial<Record<HelperName, typeof Helper>> = {};

        (Object.keys(scopeHelpers) as HelperName[]).forEach((helperName: HelperName) => {
          // @ts-ignore (don't get why TS says 'helperName' cant index 'scopeHelpers' ^^')
          const HelperClass = scopeHelpers[helperName];

          currentlyGeneratedHelper[helperName] = new HelperClass(this);
        });

        generatedHelpers[rootHelperName] = currentlyGeneratedHelper;
      }
    );

    this.helpers = generatedHelpers as ClientHelpers;

    this.addRequestInterceptor(this.injectApiVersion.bind(this)).addRequestInterceptor(
      this.checkAuthorizationHeader.bind(this)
    );
  }

  protected getCompleteUrl(path: string): string {
    return this.rootUrl + path;
  }

  public createGetRequest<EntityType extends ResponseBody | void>(
    path: string
  ): RequestInteface<EntityType> {
    return this.createRequest<EntityType>(path, 'GET');
  }

  public createPatchRequest<EntityType extends ResponseBody | void>(
    path: string
  ): RequestInteface<EntityType> {
    return this.createRequest<EntityType>(path, 'PATCH');
  }

  public createPostRequest<EntityType extends ResponseBody | void>(
    path: string
  ): RequestInteface<EntityType> {
    return this.createRequest<EntityType>(path, 'POST');
  }

  public createPutRequest<EntityType extends ResponseBody | void>(
    path: string
  ): RequestInteface<EntityType> {
    return this.createRequest<EntityType>(path, 'PUT');
  }

  public createDeleteRequest(path: string): RequestInteface {
    return this.createRequest<void>(path, 'DELETE');
  }

  public createRequest<EntityType extends ResponseBody | void>(
    path: string,
    method: HttpMethod
  ): Request<EntityType> {
    const request = new Request<EntityType>(this.getCompleteUrl(path), method, this.language);

    this.requestInterceptors.forEach((interceptor) => {
      request.addRequestInterceptor(interceptor);
    });

    this.responseInterceptors.forEach((interceptor) => {
      request.addResponseInterceptor(interceptor);
    });

    return request;
  }

  protected checkAuthorizationHeader(requestData: InterceptorRequestData): InterceptorRequestData {
    const finalOptions = { headers: {}, ...requestData.requestOptions };

    finalOptions.headers.Authorization = this.getAuthorizationHeader(finalOptions.headers);

    if (!finalOptions.headers.Authorization) {
      const error = `[OC API] try to call route ${finalOptions.url} without access token.`;
      throw new Error(error);
    }

    return { ...requestData, requestOptions: finalOptions };
  }

  protected injectApiVersion(requestData: InterceptorRequestData): InterceptorRequestData {
    const finalOptions = { headers: {}, ...requestData.requestOptions };
    const accept = finalOptions.headers.Accept ? `${finalOptions.headers.Accept};` : '';

    finalOptions.headers.Accept = `${accept}version=${this.version}`;

    return { ...requestData, requestOptions: finalOptions };
  }

  protected getAuthorizationHeader(headers: RequestHeaders = {}): string | undefined {
    if (headers.Authorization) {
      return headers.Authorization;
    }

    return this.accessToken ? `Bearer ${this.accessToken}` : undefined;
  }

  public addRequestInterceptor(
    interceptor: Interceptor<InterceptorRequestData>,
    prependInterceptor = false
  ): this {
    const addMethod = prependInterceptor ? 'unshift' : 'push';
    this.requestInterceptors[addMethod](interceptor);
    return this;
  }

  public addResponseInterceptor(interceptor: Interceptor<Response>): this {
    this.responseInterceptors.push(interceptor);
    return this;
  }

  getDynamicHelper<R>(entityName: string, parentEntity?: ParentEntity | ParentEntity[]): Helper<R> {
    let DynamicHelper: any;

    if (!parentEntity) {
      DynamicHelper = getHelper(`/${entityName}/:id`);
    }

    if (Array.isArray(parentEntity)) {
      const parentEntityName = parentEntity
        ?.map((parent) => `/${parent.name}/${parent.id}`)
        .join('');
      DynamicHelper = getHelper(parentEntityName, {
        [entityName]: getHelper(`/${entityName}/:id`),
      });
    } else if (typeof parentEntity === 'object' && parentEntity !== null) {
      DynamicHelper = getHelper(`/${parentEntity.name}/${parentEntity.id}`, {
        [entityName]: getHelper(`/${entityName}/:id`),
      });
    }

    if (!DynamicHelper) {
      throw new Error('Invalid DynamicHelper');
    }

    return new DynamicHelper(this);
  }
}

export default ClientApi;
